import { Link } from "react-router-dom";
import { useStore } from "../lib/storeContext";
import { SectionTitle } from "./ui";
import type { ScanPayload } from "../lib/types";
import { pluralize } from "../lib/util";
import { WholeHomeComposite } from "../composite/WholeHomeComposite";

// Aggregates every scan across a project's rooms. The interactive whole-home
// composite plan + stacked 3D dollhouse are added in the composite module.
export function WholeHomeSection({ projectId }: { projectId: string }) {
  const { store } = useStore();
  const rooms = store.rooms(projectId);

  const scans: { roomId: string; roomName: string; scan: ReturnType<typeof store.scans>[number] }[] = [];
  for (const room of rooms) {
    for (const scan of store.scans(room.id)) {
      scans.push({ roomId: room.id, roomName: room.payload.name, scan });
    }
  }
  if (scans.length === 0) return null;

  const interior = scans.filter((s) => !(s.scan.payload as ScanPayload).spaceKindRaw);
  const totalAreaM2 = interior.reduce((sum, s) => sum + ((s.scan.payload as ScanPayload).floorAreaSquareMeters || 0), 0);
  const totalSqFt = Math.round(totalAreaM2 * 10.7639104);

  return (
    <section>
      <SectionTitle>Whole home</SectionTitle>
      <div className="card p-5">
        <div className="flex flex-wrap items-center gap-6">
          <div>
            <div className="text-2xl font-semibold text-ink">{pluralize(scans.length, "scan")}</div>
            <div className="text-xs uppercase tracking-wide text-muted">across {pluralize(rooms.length, "room")}</div>
          </div>
          <div>
            <div className="text-2xl font-semibold text-ink">{totalSqFt.toLocaleString()} sq ft</div>
            <div className="text-xs uppercase tracking-wide text-muted">interior floor area</div>
          </div>
        </div>
        <div className="mt-4 flex flex-wrap gap-2">
          {scans.map(({ scan, roomName }) => (
            <Link key={scan.id} to={`/scans/${scan.id}`} className="chip hover:bg-accent/10">
              {roomName} · floorplan & 3D →
            </Link>
          ))}
        </div>
      </div>

      {/* Interactive whole-home composite: per-floor 2D plan + stacked 3D. */}
      <div className="mt-4">
        <WholeHomeComposite projectId={projectId} />
      </div>
    </section>
  );
}
